import React, { useEffect, useState } from 'react';
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Grid
} from '@material-ui/core';
import { ValidatorForm, TextValidator } from 'react-material-ui-form-validator';
import NumberFormat from 'react-number-format';
import swal from 'sweetalert2';
import { useSnackbar } from 'notistack';
import Help from '../help/Help';
import SubmitButton from '../controls/SubmitButton';
import NumberField from '../controls/NumberField';
import { enums } from '../../enums/enums';
import * as utility from '../../blocks/utility';
import { IParametro } from '../../redux/types/parametroActionTypes';
import { ICatalogo } from '../../entities/catalogos/ICatalogo';
import { ICliente } from '../../entities/catalogos/ICliente';
import { IEvent } from '../../interfaces/IEvent';
import { traducirButton, traducirLabel, traducirMessage } from '../../blocks/traductor';
import { evaluarSeguroGuia, IGuiaSeguroState } from './guiaSeguroHelpers';

interface ISeguroInfo {
    id: number;
    titulo: string;
}

interface SeguroGuiaProps {
    seguro: IGuiaSeguroState;
    onSeguroChange: (patch: Partial<IGuiaSeguroState>) => void;
    onAvanzar: () => void;
    cliente: ICliente;
    parametros: IParametro[];
    tiposSeguroObservacion: ICatalogo[];
}

const SeguroGuia = ({
    seguro,
    onSeguroChange,
    onAvanzar,
    cliente,
    parametros,
    tiposSeguroObservacion
}: SeguroGuiaProps) => {
    const { enqueueSnackbar } = useSnackbar();
    const [dialogOpen, setDialogOpen] = useState(false);
    const [seguroInfo, setSeguroInfo] = useState<ISeguroInfo | undefined>({ id: 0, titulo: "" });

    const valorSeguroBasico = utility.getValorParametro(parametros, 'seguroBasicoCobro', 2);
    const coberturaSeguroBasico = utility.getValorParametro(parametros, 'seguroBasicoCubre');
    const seguroDeclaradoMinimo = utility.getValorParametro(parametros, 'seguroDeclaradoMinimo');
    const seguroDeclaradoMaximo = utility.getValorParametro(parametros, 'seguroDeclaradoMaximo');

    useEffect(() => {
        ValidatorForm.addValidationRule('requiredIf', (value) => {
            if (!seguro.esSeguroMercancia) {
                return true;
            }
            if (value === null || value === undefined || value === '') {
                return false;
            }
            const n = Number(value);
            return Number.isFinite(n) && n > 0;
        });

        return function cleanup() {
            ValidatorForm.removeValidationRule('requiredIf');
        };
    }, [seguro.esSeguroMercancia]);

    const mostrarWarningExcluirSeguro = (esSeguroBasico: boolean, esSeguroMercancia: boolean) => {
        if ((esSeguroBasico || esSeguroMercancia) && cliente.esExcluirSeguro) {
            enqueueSnackbar(traducirLabel(39), { variant: 'warning' });
        }
    };

    const handleSeguroCheck = (esBasico: boolean) => {
        const valorMercancia = esBasico ? null : seguro.valorMercancia ?? undefined;
        let esSeguroBasico = seguro.esSeguroBasico;
        let esSeguroMercancia = seguro.esSeguroMercancia;

        if (!esBasico) {
            esSeguroMercancia = !esSeguroMercancia;
            if (esSeguroMercancia) {
                swal.fire({
                    titleText: traducirMessage(11),
                    text: traducirMessage(218),
                    type: 'warning'
                });
                esSeguroBasico = false;
            }
        }

        if (esBasico) {
            esSeguroBasico = !esSeguroBasico;
            if (esSeguroBasico) {
                esSeguroMercancia = false;
            }
        }

        mostrarWarningExcluirSeguro(esSeguroBasico, esSeguroMercancia);

        onSeguroChange({
            esSeguroBasico,
            esSeguroMercancia,
            valorMercancia
        });
    };

    const setValorMercancia = (event: IEvent) => {
        onSeguroChange({ valorMercancia: Number(event.target.value) });
    };

    const openDialog = (id: number) => {
        if (id === enums.seguro.basico) {
            setSeguroInfo({ id: enums.seguro.basico, titulo: traducirLabel(273) });
        } else if (id === enums.seguro.declarado) {
            setSeguroInfo({ id: enums.seguro.declarado, titulo: traducirLabel(274) });
        } else {
            setSeguroInfo({ id: 0, titulo: "" });
        }
        setDialogOpen(true);
    };

    const closeDialog = () => {
        setDialogOpen(false);
    };

    const handleSiguiente = () => {
        const resultado = evaluarSeguroGuia(
            seguro,
            seguroDeclaradoMinimo,
            seguroDeclaradoMaximo,
            cliente.esExcluirSeguro
        );

        if (resultado.tipo === 'valido') {
            onAvanzar();
            return;
        }

        if (resultado.tipo === 'requerido') {
            swal.fire({
                titleText: traducirMessage(11),
                text: traducirMessage(299),
                type: 'warning',
            });
            return;
        }

        if (resultado.tipo === 'fuera_rango') {
            const mensaje = resultado.direccion === 'min'
                ? traducirMessage(58).replace('#####', `$ ${seguroDeclaradoMinimo}`)
                : traducirMessage(57).replace('#####', `$ ${seguroDeclaradoMaximo}`);

            swal.fire({
                titleText: traducirMessage(11),
                text: mensaje,
                type: 'warning',
            });
            return;
        }

        if (!seguro.esSeguroBasico && !seguro.esSeguroMercancia) {
            swal.fire({
                titleText: traducirLabel(48),
                text: traducirLabel(362),
                type: 'warning',
                showConfirmButton: true,
                showCancelButton: true,
                cancelButtonAriaLabel: traducirButton(6),
                confirmButtonAriaLabel: traducirButton(17),
            }).then((result) => {
                if (result.value === true) {
                    onAvanzar();
                }
            });
        }
    };

    return (<>
        <Box>
            <Help comments={traducirMessage(93)} customStyle={{ top: "-30px", right: "-20px" }} />
        </Box>
        <Box mb={3} className="title text-center">
            {traducirMessage(22)}
        </Box>

        <ValidatorForm onSubmit={handleSiguiente}>
            <Grid container spacing={3} justify="center">
                <Grid item>
                    <Box
                        className={`pointer card medium-height ${seguro.esSeguroBasico ? 'selected' : ''}`}
                        style={{ padding: "10px 20px", width: "180px", height: "200px" }}
                        display="flex" flexDirection="column">
                        <Box className="title-2 green-label text-center" onClick={() => handleSeguroCheck(true)}>
                            <b>{traducirLabel(273)}</b>
                        </Box>

                        <Box className="title-4" py={1} onClick={() => handleSeguroCheck(true)}>
                            {traducirLabel(40).split('#####')[0]}
                            <NumberFormat value={coberturaSeguroBasico} displayType={'text'} prefix={'$'}
                                thousandSeparator={true} style={{ padding: '0 4px' }} />
                            {traducirLabel(40).split('#####')[1]}
                        </Box>

                        <Box display="flex" className="title-4" flexGrow={1} onClick={() => handleSeguroCheck(true)}>
                            <Box py={1} flexGrow={1}>
                                {traducirLabel(57)}
                            </Box>
                            <Box py={1}>
                                <b>${valorSeguroBasico}</b>
                            </Box>
                        </Box>

                        <Box className="title-4" pt={1}>
                            <Button color="primary" size="small" className="link-sm title-4"
                                onClick={() => openDialog(enums.seguro.basico)}>
                                {traducirLabel(317)}
                            </Button>
                        </Box>
                    </Box>
                </Grid>

                <Grid item>
                    <Box
                        className={`pointer card medium-height ${seguro.esSeguroMercancia ? 'selected' : ''}`}
                        style={{ padding: "10px 20px", width: "180px", height: "200px" }}
                        display="flex" flexDirection="column">
                        <Box className="title-2 green-label text-center" onClick={() => handleSeguroCheck(false)}>
                            <b>{traducirLabel(274)}</b>
                        </Box>

                        <Box className="title-4" py={1} onClick={() => handleSeguroCheck(false)}>
                            {traducirLabel(40).split('#####')[0]}
                            <NumberFormat value={seguroDeclaradoMaximo} displayType={'text'} prefix={'$'}
                                thousandSeparator={true} style={{ padding: '0 4px' }} />
                            {traducirLabel(40).split('#####')[1]}
                        </Box>

                        <Box className="title-4" pt={1} onClick={() => handleSeguroCheck(false)}>
                            <b>{traducirLabel(314)}</b>
                        </Box>

                        <Box className="title-4" flexGrow={1}>
                            <TextValidator
                                value={seguro.valorMercancia ?? ''}
                                name="valorMercancia"
                                onChange={setValorMercancia}
                                fullWidth
                                validators={['requiredIf']}
                                errorMessages={[traducirMessage(299)]}
                                disabled={!seguro.esSeguroMercancia}
                                InputProps={{
                                    inputComponent: NumberField,
                                    inputProps: { prefix: "$", precision: 2, maxLength: 8 },
                                }}
                            />
                        </Box>

                        <Box className="title-4" pt={1}>
                            <Button color="primary" size="small" className="link-sm title-4"
                                onClick={() => openDialog(enums.seguro.declarado)}>
                                {traducirLabel(317)}
                            </Button>
                        </Box>
                    </Box>
                </Grid>
            </Grid>

            <SubmitButton />
        </ValidatorForm>

        <Dialog open={dialogOpen} maxWidth="md" fullWidth={true}>
            <DialogTitle id="seguro-guia-dialog-title">{seguroInfo?.titulo}</DialogTitle>
            <DialogContent dividers>
                {localStorage.getItem("idioma") === 'en-US' &&
                    <DialogContentText>
                        {tiposSeguroObservacion.map((ts) => (
                            ts.id === seguroInfo?.id && ts.etiquetaTipoSeguroObservacion?.nombre !== '' &&
                            <Box mb={1} key={ts.id}>{ts.etiquetaTipoSeguroObservacion.nombre}</Box>
                        ))}
                    </DialogContentText>
                }
                {localStorage.getItem("idioma") !== 'en-US' &&
                    <DialogContentText>
                        {tiposSeguroObservacion.map((ts) => (
                            ts.id === seguroInfo?.id && ts.nombre !== '' &&
                            <Box mb={1} key={ts.id}>{ts.nombre}</Box>
                        ))}
                    </DialogContentText>
                }
            </DialogContent>
            <DialogActions>
                <Button autoFocus onClick={closeDialog} color="primary">
                    {traducirButton(16)}
                </Button>
            </DialogActions>
        </Dialog>
    </>);
};

export default SeguroGuia;
